import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import bookingsApi from "@/api/bookingsApi";
import BookingsTable from "@/components/BookingsTable/BookingsTable";
import BookingResponse from "@/model/BookingResponse";
import ApiError from "@/model/ApiError";
import "@/components/BookingsPanel/BookingsPanel.scss";

const PAGE_SIZE = 20;

interface BookingsPanelProps {
  /** Exactly one of propertyId / companyId selects which bookings are listed. */
  propertyId?: string;
  companyId?: string;
  headerAction?: ReactNode;
}

function BookingsPanel({
  propertyId,
  companyId,
  headerAction,
}: BookingsPanelProps) {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [bookings, setBookings] = useState<BookingResponse[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const [answerError, setAnswerError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState(false);

  useEffect(() => {
    if (!session || (!propertyId && !companyId)) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(false);

    const request = propertyId
      ? bookingsApi.listForProperty(
          session.accessToken,
          propertyId,
          page,
          PAGE_SIZE
        )
      : bookingsApi.listForCompany(
          session.accessToken,
          companyId as string,
          page,
          PAGE_SIZE
        );

    request
      .then((result) => {
        if (cancelled) {
          return;
        }
        setBookings(result.content ?? []);
        setTotalPages(result.totalPages ?? 0);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, propertyId, companyId, page]);

  if (!session) {
    return null;
  }

  const handleCancel = async (booking: BookingResponse) => {
    const message =
      booking.type === "OWNER_BLOCK"
        ? t("bookings.cancelBlockConfirm")
        : booking.status === "CONFIRMED"
          ? t("bookings.cancelPaidConfirm")
          : t("bookings.cancelConfirm");
    if (!booking.id || !window.confirm(message)) {
      return;
    }

    setCancellingId(booking.id);
    setCancelError(false);

    try {
      const updated = await bookingsApi.cancelBooking(
        session.accessToken,
        booking.id
      );
      setBookings((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
    } catch {
      setCancelError(true);
    } finally {
      setCancellingId(null);
    }
  };

  const replaceBooking = (updated: BookingResponse) =>
    setBookings((current) =>
      current.map((item) => (item.id === updated.id ? updated : item))
    );

  /** Accepting captures the guest's payment; declining releases it, with an optional message. */
  const answer = async (booking: BookingResponse, accept: boolean) => {
    if (!booking.id) {
      return;
    }
    let message: string | null = null;
    if (accept) {
      if (!window.confirm(t("bookings.request.acceptConfirm"))) {
        return;
      }
    } else {
      message = window.prompt(t("bookings.request.declinePrompt"), "");
      if (message === null) {
        return;
      }
    }

    setAnsweringId(booking.id);
    setAnswerError(null);
    try {
      const updated = accept
        ? await bookingsApi.acceptBooking(session.accessToken, booking.id)
        : await bookingsApi.declineBooking(session.accessToken, booking.id, {
            message: message?.trim() || undefined,
          });
      replaceBooking(updated);
    } catch (err) {
      setAnswerError(
        err instanceof ApiError && err.status === 409
          ? t("bookings.request.alreadyAnswered")
          : err instanceof ApiError && err.status === 502
            ? t("bookings.request.paymentError")
            : t("bookings.request.genericError")
      );
    } finally {
      setAnsweringId(null);
    }
  };

  const pendingRequests = bookings.filter((booking) => booking.status === "AWAITING_HOST").length;

  return (
    <div className="bookings-panel">
      <div className="bookings-panel__header">
        <h2>{t("bookings.title")}</h2>
        {headerAction}
      </div>

      {pendingRequests > 0 && (
        <p className="bookings-panel__requests" role="status">
          {t("bookings.request.pending", { count: pendingRequests })}
        </p>
      )}

      {answerError && <p className="bookings-panel__error">{answerError}</p>}

      {cancelError && (
        <p className="bookings-panel__error">{t("bookings.cancelError")}</p>
      )}

      {isLoading && <p>{t("bookings.loading")}</p>}
      {!isLoading && error && <p role="alert">{t("bookings.error")}</p>}
      {!isLoading && !error && bookings.length === 0 && (
        <p>{t("bookings.empty")}</p>
      )}

      {!isLoading && !error && bookings.length > 0 && (
        <>
          <BookingsTable
            bookings={bookings}
            showProperty={!propertyId}
            cancellingId={cancellingId}
            onCancel={handleCancel}
            answeringId={answeringId}
            onAccept={(booking) => answer(booking, true)}
            onDecline={(booking) => answer(booking, false)}
          />

          <div className="bookings-panel__pagination">
            <button
              type="button"
              onClick={() => setPage((current) => current - 1)}
              disabled={page <= 0}
            >
              {t("pagination.previous")}
            </button>
            <span>
              {t("pagination.pageInfo", { page: page + 1, totalPages })}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={page + 1 >= totalPages}
            >
              {t("pagination.next")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default BookingsPanel;
export type { BookingsPanelProps };
